import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, RefreshCw, PlayCircle, AlertTriangle } from "lucide-react";

/**
 * Peppol Turn 2 — Vue Super-admin Wérko.
 *
 * Liste des tenants, bouton "Onboarder" (si adresse structurée + VAT + IBAN + phone
 * + contact_email remplis), bouton "Rafraîchir statut", historique des tentatives.
 *
 * Accès protégé côté backend : whitelist SUPER_ADMIN_EMAILS. Le rendu affiche un
 * message d'erreur si l'utilisateur n'est pas whitelisté (403 renvoyé par l'API).
 */
function StatusBadge({ status, rejectReason }) {
  const map = {
    null: { label: "Non onboardé", color: "bg-slate-700 text-slate-200" },
    CREATED: { label: "Créé", color: "bg-blue-900/40 text-blue-200" },
    APPROVED: { label: "En cours", color: "bg-amber-900/40 text-amber-200" },
    ONBOARDED: { label: "Onboardé ✓", color: "bg-green-900/40 text-green-200" },
    REJECTED: { label: `Rejeté: ${rejectReason || "?"}`, color: "bg-red-900/40 text-red-200" },
    OFFBOARDED: { label: "Désactivé", color: "bg-slate-700 text-slate-200" },
  };
  const cfg = map[status] || map["null"];
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cfg.color}`}>{cfg.label}</span>;
}

export default function PeppolTenantsAdminPage() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [error, setError] = useState(null);
  const [requests, setRequests] = useState([]);

  async function reload() {
    setLoading(true);
    try {
      const [t, r] = await Promise.all([
        api.get("/admin/tenants"),
        api.get("/admin/tenants/peppol/onboarding-requests?limit=50"),
      ]);
      setTenants(t.data.tenants || []);
      setRequests(r.data.requests || []);
      setError(null);
    } catch (e) {
      if (e?.response?.status === 403) {
        setError("Accès réservé aux super-admins Wérko.");
      } else {
        setError(formatApiError(e));
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { reload(); }, []);

  async function handleOnboard(company) {
    if (!window.confirm(`Onboarder ${company.name} sur Peppol Sandbox WK ? Cette action est irréversible côté WK.`)) return;
    setBusy((b) => ({ ...b, [company.id]: "onboard" }));
    try {
      const { data } = await api.post(`/admin/tenants/${company.id}/peppol/onboard`);
      toast.success(`${company.name} onboardé — supplier ${data.company.peppol_wk_supplier_id?.slice(0,8)}…`);
      reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "mapping") {
        toast.error(`Champs manquants : ${detail.detail}`);
      } else if (detail?.error === "wk_api") {
        toast.error(`Erreur WK (HTTP ${detail.http_code}) — voir logs`);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy((b) => ({ ...b, [company.id]: null }));
    }
  }

  async function handleRefreshStatus(company) {
    setBusy((b) => ({ ...b, [company.id]: "refresh" }));
    try {
      const { data } = await api.get(`/admin/tenants/${company.id}/peppol/supplier-status`);
      toast.success(`Statut mis à jour : ${data.wk_status}`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [company.id]: null }));
    }
  }

  if (loading) return <div className="text-slate-400" data-testid="admin-loading">Chargement…</div>;
  if (error) return (
    <div className="rounded-md border border-red-800/50 bg-red-950/40 p-4 text-sm text-red-200" data-testid="admin-error">
      <AlertTriangle className="mb-2 inline h-4 w-4" /> {error}
    </div>
  );

  return (
    <div className="space-y-6 p-6" data-testid="peppol-tenants-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-100">Peppol — Tenants</h1>
          <p className="mt-1 text-sm text-slate-400">
            Super-admin Wérko — Gestion des Supplier Peppol Wolters Kluwer Flowin (Sandbox).
          </p>
        </div>
        <Button onClick={reload} variant="outline" size="sm" data-testid="reload-btn">
          <RefreshCw className="mr-2 h-4 w-4" /> Rafraîchir
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-sm text-slate-300">Tenants ({tenants.length})</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tenants-table">
              <thead>
                <tr className="border-b border-slate-800 text-left text-xs uppercase text-slate-500">
                  <th className="py-2 pr-4">Nom</th>
                  <th className="py-2 pr-4">VAT</th>
                  <th className="py-2 pr-4">Prêt ?</th>
                  <th className="py-2 pr-4">Supplier</th>
                  <th className="py-2 pr-4">Statut</th>
                  <th className="py-2 pr-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.id} className="border-b border-slate-800/60" data-testid={`tenant-row-${t.id}`}>
                    <td className="py-2 pr-4 font-medium text-slate-200">{t.name}</td>
                    <td className="py-2 pr-4 font-mono text-xs text-slate-400">{t.vat_number || "—"}</td>
                    <td className="py-2 pr-4">
                      {t.onboarding_ready ? (
                        <CheckCircle2 className="h-4 w-4 text-green-400" />
                      ) : (
                        <span className="text-xs text-red-400" title={`Manque: ${t.missing_fields.join(", ")}`}>
                          <XCircle className="mr-1 inline h-4 w-4" />
                          {t.missing_fields.length} manquant(s)
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs text-slate-500">
                      {t.peppol_wk_supplier_id ? t.peppol_wk_supplier_id.slice(0, 8) + "…" : "—"}
                    </td>
                    <td className="py-2 pr-4">
                      <StatusBadge status={t.peppol_wk_onboarding_status} rejectReason={t.peppol_wk_reject_reason} />
                    </td>
                    <td className="py-2 pr-2 text-right">
                      {!t.peppol_wk_supplier_id ? (
                        <Button
                          size="sm"
                          disabled={!t.onboarding_ready || busy[t.id] === "onboard"}
                          onClick={() => handleOnboard(t)}
                          data-testid={`onboard-btn-${t.id}`}
                        >
                          <PlayCircle className="mr-1 h-4 w-4" />
                          {busy[t.id] === "onboard" ? "…" : "Onboarder"}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy[t.id] === "refresh"}
                          onClick={() => handleRefreshStatus(t)}
                          data-testid={`refresh-btn-${t.id}`}
                        >
                          <RefreshCw className="mr-1 h-4 w-4" />
                          {busy[t.id] === "refresh" ? "…" : "Statut"}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm text-slate-300">Historique des onboardings ({requests.length})</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="onboarding-requests-table">
              <thead>
                <tr className="border-b border-slate-800 text-left text-xs uppercase text-slate-500">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Tenant</th>
                  <th className="py-2 pr-4">Trigger</th>
                  <th className="py-2 pr-4">Statut</th>
                  <th className="py-2 pr-4">Supplier / Erreur</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id} className="border-b border-slate-800/60" data-testid={`request-row-${r.id.slice(0,8)}`}>
                    <td className="py-2 pr-4 text-xs text-slate-400">
                      {new Date(r.created_at).toLocaleString("fr-BE", { hour12: false })}
                    </td>
                    <td className="py-2 pr-4 text-slate-200">{r.company_name_snapshot}</td>
                    <td className="py-2 pr-4 text-xs text-slate-500">
                      {r.triggered_by_email} <span className="text-slate-600">({r.triggered_by_role})</span>
                    </td>
                    <td className="py-2 pr-4">
                      <Badge variant={r.status === "created" || r.status === "onboarded" ? "default" : "destructive"}>
                        {r.status}
                      </Badge>
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs text-slate-500">
                      {r.wk_supplier_id
                        ? r.wk_supplier_id.slice(0, 12) + "…"
                        : <span className="text-red-400">{r.error_stage}: {(r.error_detail || "").slice(0, 60)}…</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
